<template>
  <div class="flex mt-20 ml-12 space-x-8">
    <!-- Sidebar Buttons -->
    <div class="flex flex-col space-y-4">
      <h1 class="text-4xl font-thin my-6 text-start mt-0 font-extrabold">Alerts</h1>

      <button
        @click="setFetchString('logs/get_error_log_details')"
        :class="buttonClass('logs/get_error_log_details', 'bg-slate-300', 'bg-blue-white')"
        class="p-3 text-black rounded-lg w-[150px] h-[50px]"
      >
        <b>ALL</b>
      </button>

      <button
        @click="setFetchString('logs/get_red_error_log_details')"
        :class="buttonClass('logs/get_red_error_log_details', 'bg-red-800', 'bg-red-600')"
        class="text-white p-3 rounded-lg w-[150px] h-[50px]"
      >
        <b>High</b>
      </button>

      <button
        @click="setFetchString('logs/get_amber_error_log_details')"
        :class="buttonClass('logs/get_amber_error_log_details', 'bg-amber-600', 'bg-amber-500')"
        class="text-white p-3 rounded-lg w-[150px] h-[50px]"
      >
        <b>Medium</b>
      </button>

      <button
        @click="setFetchString('logs/get_green_error_log_details')"
        :class="buttonClass('logs/get_green_error_log_details', 'bg-purple-600', 'bg-purple-500')"
        class="text-white p-3 rounded-lg w-[150px] h-[50px]"
      >
        <b>Low</b>
      </button>

      <button
        @click="openOverlay(guideContent)"
        class="p-4 bg-gray-800 text-white rounded-lg w-[150px] h-[50px]"
      >
        GUIDE
      </button>
    </div>

    <!-- Main Section -->
    <div class="flex flex-col w-full">
      <!-- Date Range Selector -->
      <div class="mb-4">
        <button @click="setDateRange(1)" :class="rangeButtonClass(1)">Today</button>
        <button @click="setDateRange(7)" :class="rangeButtonClass(7)">Last 7 Days</button>
        <button @click="setDateRange('custom')" :class="rangeButtonClass('custom')">Custom</button>

        <div v-if="activeRange === 'custom'" class="flex space-x-2 mt-2 items-center">
          <input type="date" v-model="startDate" class="p-2 border rounded-md" />
          <input type="date" v-model="endDate" class="p-2 border rounded-md" />
        </div>
      </div>

      <!-- Alert Table -->
      <div v-if="fetchString">
        <AlertTable
          :fetch-string="fetchString"
          :start-date="startDate"
          :end-date="endDate"
          :page="currentPage"
        />
      </div>
      <div v-else class="text-center text-gray-500 text-lg mt-12">
        Please select a location from the sidebar to view alerts.
      </div>
    </div>

    <!-- Overlay -->
    <div v-if="isOverlayVisible" class="fixed inset-0 flex items-left justify-center bg-black bg-opacity-50 z-50">
      <div class="bg-white p-6 rounded-lg shadow-lg mt-6 mb-6 w-2/3 max-w-full overflow-y-auto max-h-[90vh] text-left">
        <div v-html="overlayContent"></div>
        <button @click="closeOverlay" class="mt-4 p-2 bg-blue-500 text-white rounded-lg">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useLocationStore } from '@/stores/locationStore';
import AlertTable from '@/components/AlertTable.vue';

const locationStore = useLocationStore();
const fetchString = ref('');
const activeButton = ref('logs/get_error_log_details');
const startDate = ref('');
const endDate = ref('');
const activeRange = ref('custom');
const currentPage = ref(1);

// Overlay
const isOverlayVisible = ref(false);
const overlayContent = ref('');
const guideContent = `
  <div style="font-family: sans-serif; line-height: 1.6;">
    <h2 style="color: red;"><b>High Alert Report - High</b></h2>
    <p>A <b>High</b> system alert has been triggered after detecting the listed faults within the last 24 hours. Immediate maintenance is required, to rectify the failure and prevent disruption to traffic operations.</p>
    <br>
    <p><b>Action suggested:</b> Reactive maintenance must be performed at once.</p>
    <br>
    <h2 style="color: orange;"><b>Medium Alert Report - Medium</b></h2>
    <p>A <b>Medium</b> system alert has been triggered after detecting the listed faults that occurred 3 times or more within the last 24 hours. Urgent maintenance is required, to rectify the failure and prevent potential disruption to traffic operations.</p>
    <br>
    <p><b>Action Suggested:</b> Reactive maintenance must be performed within the next 72 hours.</p>
    <br>
    <h2 style="color: purple;"><b>Low Alert Report - Moderate</b></h2>
    <p>A <b>Low</b> system alert has been triggered after detecting the listed faults that occurred 3 times or more within the last 7 days. Whilst these issues do not pose an immediate risk, planned or reactive maintenance is recommended to ensure the system remains operational and doesn't cause disruption to traffic operations.</p>
    
    <p><b>Action Suggested:</b> Planned or reactive maintenance should be performed within the next 7 days.</p>
  </div>
`;

// Button styling logic
const buttonClass = (type, activeClass, inactiveClass) => {
  return activeButton.value === type ? activeClass : inactiveClass;
};
const rangeButtonClass = (range) => {
  return {
    'p-2 text-gray-500 hover:text-blue-500': true,
    'font-semibold text-blue-600': activeRange.value === range,
  };
};

// React to location or filter changes
watch(
  () => [
    locationStore.selectedLocation?.id,
    activeButton.value,
    startDate.value,
    endDate.value
  ],
  updateFetchString,
  { immediate: true }
);

function updateFetchString() {
  const locId = locationStore.selectedLocation?.id;
  if (!locId) {
    fetchString.value = '';
    console.log("No location selected");
    return;
  }

  const base = activeButton.value;
  const dateParams =
    startDate.value && endDate.value
      ? `&start_date=${startDate.value}&end_date=${endDate.value}`
      : '';

  fetchString.value = `${base}?location=${locId}${dateParams}`;
  console.log("FetchString: ", fetchString.value);
}

function setFetchString(type) {
  activeButton.value = type;
  currentPage.value = 1;
  updateFetchString();
}

function setDateRange(days) {
  const today = new Date();
  activeRange.value = days;

  if (days === 'custom') {
    startDate.value = '';
    endDate.value = '';
  } else {
    const past = new Date();
    past.setDate(today.getDate() - days);
    startDate.value = past.toISOString().split('T')[0];
    endDate.value = new Date().toISOString().split('T')[0];
  }

  updateFetchString();
}

function openOverlay(content) {
  overlayContent.value = content;
  isOverlayVisible.value = true;
}

function closeOverlay() {
  isOverlayVisible.value = false;
}
</script>

<style scoped>
button:hover {
  cursor: pointer;
}
</style>
